import React, { useState, useContext, useEffect } from "react";
import AppContext from "context/Context";
import { Col, Container, Row, Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import styles from "./landing.module.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import * as General from "common/general";

const Manuals = () => {
	const {
		config: { isRTL },
	} = useContext(AppContext);

	const [t] = useTranslation();

	const [manuals, setManuals] = useState({});
	const [loadingFile, setLoadingFile] = useState(null); // 👈 track which file is downloading
	const [cache, setCache] = useState({}); // 👈 cache downloaded blobs

	// Remove unwanted CSS once
	useEffect(() => {
		const cssToRemove = document.getElementById("css-to-remove");
		if (cssToRemove) cssToRemove.remove();
	}, []);

	// Fetch manuals list ONLY (no files)
	useEffect(() => {
		const fetchData = async () => {
			try {
				const response = await General.GetUserManuals();

				if (response?.data?.dataBaseContent) {
					const mappedData = response.data.dataBaseContent.map((item) => ({
						title: item.title,
						to: item.to,
						parentTitle: item.parentTitle,
						parentOrder: item.parentOrder,
						order: item.order,
					}));

					const groupedData = mappedData.reduce((acc, item) => {
						if (!acc[item.parentTitle]) {
							acc[item.parentTitle] = {
								items: [],
								parentOrder: item.parentOrder,
							};
						}
						acc[item.parentTitle].items.push(item);
						return acc;
					}, {});

					const sortedGroupedData = Object.entries(groupedData)
						.sort(([, a], [, b]) => a.parentOrder - b.parentOrder)
						.reduce((acc, [key, value]) => {
							acc[key] = value.items.sort((a, b) => a.order - b.order);
							return acc;
						}, {});

					setManuals(sortedGroupedData);
				}
			} catch (error) {
				console.error("Error fetching manual data:", error);
			}
		};

		fetchData();
	}, [t]);

	// Base64 → Blob
	const createBlobUrlFromBase64 = (base64, extension) => {
		if (!base64) return null;

		const cleaned = base64.replace(/[^A-Za-z0-9+/=]/g, "");
		const byteCharacters = atob(cleaned);

		const byteArray = Uint8Array.from(byteCharacters, (c) => c.charCodeAt(0));

		const mimeType = extension === "pdf" ? "application/pdf" : `image/${extension}`;

		const blob = new Blob([byteArray], { type: mimeType });
		return URL.createObjectURL(blob);
	};

	// Download handler
	const handleDownload = async (e, item) => {
		e.preventDefault();

		// If already cached → open immediately
		if (cache[item.to]) {
			window.open(cache[item.to]);
			return;
		}

		try {
			setLoadingFile(item.to);

			const fileResponse = await General.GetPublicFile(item.to);
			const extension = item.to?.split(".").pop()?.toLowerCase();

			const blobUrl = createBlobUrlFromBase64(fileResponse.message, extension);

			if (!blobUrl) return;

			// Cache it
			setCache((prev) => ({ ...prev, [item.to]: blobUrl }));

			// Trigger download
			const link = document.createElement("a");
			link.href = blobUrl;
			link.download = item.to;
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
		} catch (err) {
			console.error("Download failed", err);
		} finally {
			setLoadingFile(null);
		}
	};

	return (
		<div className={styles.backgroundWhite}>
			<Container className={`pt-5 ${styles.addPageHeight}`}>
				<h2 className={`${isRTL ? styles.landingTitleAr : styles.landingTitle} mb-4`}>{t("User Manual")}</h2>

				<Row>
					{Object.entries(manuals).map(([parentTitle, items]) => (
						<Col lg={6} md={12} className="mb-4" key={parentTitle}>
							<div className="manual-group">
								<h2 className={`${isRTL ? styles.manualTitleAr : styles.manualTitle} ${styles.manualPadding20} mb-2`}>{t(parentTitle)}</h2>

								{items.map((item) => (
									<div className={styles.downloading} key={item.to}>
										<button
											onClick={(e) => handleDownload(e, item)}
											className={`btn btn-link text-600 ${styles.downloadinglink} ${styles.manualPadding50}`}
											disabled={loadingFile === item.to}
											style={{ textDecoration: "none" }}
										>
											{loadingFile === item.to ? (
												<>
													<Spinner animation="border" size="sm" className="me-2" />
													{t("Downloading...")}
												</>
											) : (
												<>
													<FontAwesomeIcon icon="fa-solid fa-download" className={styles.downloadingButton} />
													<span className={styles.downloadingText}>{t(item.title)}</span>
												</>
											)}
										</button>
									</div>
								))}
							</div>
						</Col>
					))}
				</Row>
			</Container>
		</div>
	);
};

export default Manuals;
